import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaClock, FaExclamationCircle } from "react-icons/fa";
import api from "../api/axios";
import Navbar from "./Navbar";
import EmployerSidebar, { ChevronDownIcon } from "./EmployerSidebar";

const COLORS = { blue: "#9333EA", dark: "#1E1033" };

const STATUS_STYLES = {
  pending: { bg: "#FFFBEB", text: "#B45309" },
  confirmed: { bg: "#F0FDF4", text: "#16A34A" },
  skipped: { bg: "#F3F4F6", text: "#6B7280" },
};

const MILESTONE_LABELS = { 7: "7 days", 30: "30 days", 60: "60 days", 90: "90 days" };

export default function EmployerRetentions() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const [retentions, setRetentions] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [statusFilter, setStatusFilter] = useState("");
  const [dueOnly, setDueOnly] = useState(false);
  const [page, setPage] = useState(1);

  const [confirmModal, setConfirmModal] = useState(null); // retention being confirmed
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadRetentions = useCallback(() => {
    setLoading(true);
    setError("");
    api
      .get("/placement-retentions", {
        params: {
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(dueOnly ? { due: 1 } : {}),
          page,
        },
      })
      .then((res) => {
        const body = res.data;
        setRetentions(body.data ?? []);
        setPagination({
          current_page: body.current_page ?? 1,
          last_page: body.last_page ?? 1,
          total: body.total ?? (body.data ?? []).length,
        });
      })
      .catch(() => setError("Couldn't load retention milestones."))
      .finally(() => setLoading(false));
  }, [statusFilter, dueOnly, page]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    loadRetentions();
  }, [loadRetentions, navigate]);

  // Filters reset pagination to page 1
  const onStatusChange = (val) => {
    setStatusFilter(val);
    setPage(1);
  };
  const onDueOnlyChange = (val) => {
    setDueOnly(val);
    setPage(1);
  };

  const openConfirm = (retention) => {
    setConfirmModal(retention);
    setRemarks("");
  };

  const submitConfirm = async () => {
    if (!confirmModal) return;
    setSubmitting(true);
    try {
      const res = await api.put(`/placement-retentions/${confirmModal.id}/confirm`, {
        remarks: remarks.trim() || undefined,
      });
      setRetentions((prev) =>
        prev.map((r) => (r.id === confirmModal.id ? { ...r, ...res.data } : r))
      );
      setConfirmModal(null);
      setRemarks("");
    } catch (err) {
      const msg =
        err?.response?.data?.errors?.status?.[0] ||
        err?.response?.data?.message ||
        "Couldn't confirm this milestone.";
      setError(msg);
      setConfirmModal(null);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const isOverdue = (r) => r.status === "pending" && new Date(r.due_date) < new Date().setHours(0, 0, 0, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="retentions" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="mx-auto max-w-5xl px-4 py-6">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? "Hide" : "Show"}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? "rotate-180" : ""}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} activeTab="retentions" mobile />}

            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">
                  Retention Milestones
                </h1>
                <p className="text-sm text-gray-500">
                  Confirm candidates who are still placed at each retention checkpoint.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={dueOnly}
                    onChange={(e) => onDueOnlyChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 accent-[#9333EA]"
                  />
                  Due now
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => onStatusChange(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="skipped">Skipped</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
            )}

            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading retention milestones...</div>
            ) : retentions.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-400">
                {dueOnly || statusFilter
                  ? "No milestones match these filters."
                  : "No retention milestones yet — these are created automatically once a placement is confirmed."}
              </div>
            ) : (
              <>
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Candidate</th>
                        <th className="px-4 py-3">Role</th>
                        <th className="px-4 py-3">Milestone</th>
                        <th className="px-4 py-3">Due</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {retentions.map((r) => {
                        const style = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                        const overdue = isOverdue(r);
                        return (
                          <tr key={r.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium" style={{ color: COLORS.dark }}>
                              {r.placement?.candidate?.name ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {r.placement?.job_posting?.title ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {MILESTONE_LABELS[r.milestone_days] ?? `${r.milestone_days} days`}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              <span className="inline-flex items-center gap-1.5">
                                {overdue && <FaExclamationCircle size={12} className="text-red-500" />}
                                {formatDate(r.due_date)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium capitalize"
                                style={{ backgroundColor: style.bg, color: style.text }}
                              >
                                {r.status === "pending" && <FaClock size={10} />}
                                {r.status === "confirmed" && <FaCheckCircle size={10} />}
                                {r.status}
                              </span>
                              {r.status === "confirmed" && r.confirmed_at && (
                                <div className="mt-1 text-[11px] text-gray-400">
                                  Confirmed {formatDate(r.confirmed_at)}
                                </div>
                              )}
                              {r.remarks && (
                                <div className="mt-1 text-[11px] text-gray-400 max-w-[180px] truncate" title={r.remarks}>
                                  "{r.remarks}"
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {r.status === "pending" && (
                                <button
                                  onClick={() => openConfirm(r)}
                                  className="flex items-center gap-1 text-sm font-medium ml-auto"
                                  style={{ color: "#16A34A" }}
                                >
                                  <FaCheckCircle size={12} /> Confirm
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.last_page > 1 && (
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <span>
                      Page {pagination.current_page} of {pagination.last_page} · {pagination.total} total
                    </span>
                    <div className="flex gap-2">
                      <button
                        disabled={pagination.current_page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="rounded-md border border-gray-300 px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        disabled={pagination.current_page >= pagination.last_page}
                        onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                        className="rounded-md border border-gray-300 px-3 py-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onMouseDown={(e) => e.target === e.currentTarget && setConfirmModal(null)}
        >
          <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="px-6 py-4 bg-gradient-to-r from-[#7C3AED] to-[#DB2777]">
              <h2 className="text-base font-semibold text-white">Confirm retention</h2>
            </div>
            <div className="px-6 py-5">
              <p className="mb-3 text-sm text-gray-500">
                {confirmModal.placement?.candidate?.name ?? "This candidate"} —{" "}
                {MILESTONE_LABELS[confirmModal.milestone_days] ?? `${confirmModal.milestone_days} days`}{" "}
                milestone, due {formatDate(confirmModal.due_date)}.
              </p>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Remarks <span className="text-gray-400">(optional)</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                maxLength={255}
                placeholder="e.g. Confirmed with hiring manager, still active…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
              />
              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={() => setConfirmModal(null)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitConfirm}
                  disabled={submitting}
                                  className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] hover:opacity-90"
                >
                  {submitting ? "Confirming…" : "Confirm milestone"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}